interface SearchChannelsSectionProps {
  onOpenForm: () => void;
}

const channels = [
  {
    icon: 'ri-robot-2-line',
    title: 'AI Search',
    subtitle: 'ChatGPT, Gemini, Perplexity & more',
    desc: 'AI tools now answer questions by citing businesses directly. If your site isn\'t structured for this, you\'re invisible to the fastest-growing search channel.',
    stat: '40%',
    statLabel: 'of searches are AI-assisted in 2026',
    highlight: true,
  },
  {
    icon: 'ri-mic-line',
    title: 'Voice Search',
    subtitle: 'Siri, Alexa, Google Assistant',
    desc: 'Voice queries are conversational and local. We optimize your content for natural spoken language patterns so you\'re found when people ask rather than type.',
    stat: '58%',
    statLabel: 'of consumers use voice search daily',
    highlight: false,
  },
  {
    icon: 'ri-global-line',
    title: 'Browser Search',
    subtitle: 'Google, Bing & organic rankings',
    desc: 'Traditional SEO still drives massive high-intent traffic. We build real authority that compounds month over month — ranking you where your customers look first.',
    stat: '27.6%',
    statLabel: 'of clicks go to the #1 result',
    highlight: false,
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Maps & Local Search',
    subtitle: 'Google Maps, Apple Maps & "near me"',
    desc: 'Local intent searches convert 5x faster than generic ones. We dominate your local map pack — so nearby customers choose you before they even reach your website.',
    stat: '76%',
    statLabel: 'of local searches result in a visit',
    highlight: false,
  },
];

export default function SearchChannelsSection({ onOpenForm }: SearchChannelsSectionProps) {
  return (
    <section id="search-channels" className="bg-[#0d1117] py-24 md:py-32 relative overflow-hidden">
      {/* Glows */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">

        {/* Storytelling intro — the 2026 shift */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <p className="text-emerald-400 text-xs font-bold tracking-widest uppercase mb-4">The 2026 Search Shift</p>
          <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6">
            Built for How People{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
              Actually Search Today
            </span>
          </h2>
          {/* The depth paragraph */}
          <div className="bg-white/[0.04] border border-white/8 rounded-2xl px-7 py-6 text-left mb-4">
            <p className="text-white/70 text-base leading-relaxed mb-4">
              The way customers find businesses changed dramatically. People aren't just typing into Google anymore — they're asking AI tools, using voice commands, and searching maps. Most businesses built their website for 2015 search behavior.
            </p>
            <p className="text-white/55 text-base leading-relaxed">
              <strong className="text-emerald-400 font-semibold">Your competitors aren't optimized for this yet.</strong> That's the window. We build your entire digital presence to be found across every channel that matters right now.
            </p>
          </div>
          <p className="text-white/35 text-sm">
            Your new website is engineered to be discovered across all four search platforms simultaneously.
          </p>
        </div>

        {/* 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {channels.map((ch) => (
            <div
              key={ch.title}
              className={`group relative rounded-2xl p-7 md:p-8 border transition-all duration-300 cursor-default ${
                ch.highlight
                  ? 'bg-gradient-to-br from-emerald-500/10 to-teal-500/8 border-emerald-500/30 hover:border-emerald-500/50'
                  : 'bg-white/[0.04] border-white/8 hover:border-emerald-500/25 hover:bg-white/[0.06]'
              }`}
            >
              {ch.highlight && (
                <div className="absolute top-4 right-4 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-2.5 py-0.5">
                  <p className="text-emerald-300 text-xs font-bold">Fastest Growing</p>
                </div>
              )}

              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/15 border border-emerald-500/20 mb-5 group-hover:from-emerald-500/30 transition-all duration-300">
                <i className={`${ch.icon} text-emerald-300`} style={{ fontSize: '22px' }}></i>
              </div>

              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="text-white font-bold text-lg md:text-xl mb-1">{ch.title}</h3>
                  <p className="text-emerald-400/70 text-xs font-medium">{ch.subtitle}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-2xl font-black bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent leading-none">{ch.stat}</p>
                </div>
              </div>

              <p className="text-white/55 text-sm leading-relaxed mb-4">{ch.desc}</p>
              <p className="text-white/28 text-xs italic">{ch.statLabel}</p>

              <div className="absolute bottom-0 left-7 right-7 h-px bg-gradient-to-r from-transparent via-emerald-500/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Inline CTA */}
        <div className="bg-gradient-to-r from-emerald-500/12 to-teal-500/8 border border-emerald-500/20 rounded-2xl px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-lg md:text-xl mb-1.5">
              Ready to be found everywhere your customers search?
            </p>
            <p className="text-white/45 text-sm">Your competitors aren't optimized for 2026. This is your competitive edge.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={onOpenForm}
              className="whitespace-nowrap bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity cursor-pointer"
            >
              See What Your Site Could Look Like
            </button>
            <a
              href="tel:8004559670"
              className="whitespace-nowrap flex items-center justify-center gap-2 bg-white/8 border border-white/15 text-white font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-white/14 transition-all cursor-pointer"
            >
              <i className="ri-phone-fill text-emerald-400" style={{ fontSize: '14px' }}></i>
              Call Us Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
